import { createContext, useContext, useState, useCallback } from 'react'
import {
  getCurrentUser,
  setCurrentUser,
  clearCurrentUser,
  isAdmin,
  isSuperAdmin,
  isStoreAdmin,
  isStaff,
  canManage,
  canManageGlobally,
  canManageStore,
  isReadOnly,
} from '../auth'
import { logOperation, AUDIT_ACTION } from '../services/auditService'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getCurrentUser())

  const signOut = useCallback(async () => {
    const current = getCurrentUser()
    if (current) {
      await logOperation(current, AUDIT_ACTION.LOGOUT, { targetType: 'session' })
    }
    clearCurrentUser()
    setUser(null)
  }, [])

  const loginSuccess = useCallback((session) => {
    setCurrentUser(session)
    setUser(session)
  }, [])

  const value = {
    user,
    loading: false,
    signOut,
    loginSuccess,
    isAdmin: isAdmin(user),
    isSuperAdmin: isSuperAdmin(user),
    isStoreAdmin: isStoreAdmin(user),
    isStaff: isStaff(user),
    canManage: canManage(user),
    canManageGlobally: canManageGlobally(user),
    canManageStore: canManageStore(user),
    isReadOnly: isReadOnly(user),
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return ctx
}
